import React from 'react';
import { MessageCircleIcon, BellIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
import { Button } from '../ui/Button';
import { FloatingConfig, FloatingDevice, FloatingMessage } from '../../types';
import { DEFAULT_ACTIVITY_MESSAGES } from '../../data/floating';

/**
 * Panel «Elementos flotantes» del constructor.
 *
 * Guarda su configuración dentro de `pageConfig.floating`, es decir, en la
 * propia landing: cada página puede tener ambos elementos, solo uno o ninguno,
 * y la misma configuración alimenta el constructor, la vista previa y la
 * landing pública, porque todas renderizan el mismo componente.
 */

interface FloatingSettingsProps {
  value?: FloatingConfig;
  onChange: (config: FloatingConfig) => void;
}
const DEVICES: {
  value: FloatingDevice;
  label: string;
}[] = [{
  value: 'desktop',
  label: 'Escritorio'
}, {
  value: 'tablet',
  label: 'Tablet'
}, {
  value: 'mobile',
  label: 'Móvil'
}];

/**
 * Fuente única de los avisos neutros por defecto: la misma que reciben las
 * landings nuevas, para que el constructor y el valor guardado no se separen.
 */
const DEFAULT_MESSAGES = DEFAULT_ACTIVITY_MESSAGES;
const POSITIONS = [{
  value: 'derecha',
  label: 'Abajo a la derecha'
}, {
  value: 'izquierda',
  label: 'Abajo a la izquierda'
}];
export function FloatingSettings({
  value,
  onChange
}: FloatingSettingsProps) {
  const config = value ?? {};
  const whatsapp = config.whatsapp ?? {
    enabled: false
  };
  const activity = config.activity ?? {
    enabled: false
  };
  const messages: FloatingMessage[] = activity.messages ?? [];
  function setWhatsapp(partial: Partial<typeof whatsapp>): void {
    onChange({
      ...config,
      whatsapp: {
        ...whatsapp,
        ...partial
      }
    });
  }
  function setActivity(partial: Partial<typeof activity>): void {
    onChange({
      ...config,
      activity: {
        ...activity,
        ...partial
      }
    });
  }
  function toggleDevice(current: FloatingDevice[] | undefined, device: FloatingDevice): FloatingDevice[] {
    const list = current && current.length > 0 ? current : ['desktop', 'tablet', 'mobile'] as FloatingDevice[];
    return list.includes(device) ? list.filter(d => d !== device) : [...list, device];
  }
  function deviceRow(current: FloatingDevice[] | undefined, apply: (next: FloatingDevice[]) => void) {
    const list = current && current.length > 0 ? current : ['desktop', 'tablet', 'mobile'];
    return <div className="flex flex-wrap gap-2" data-id="element-388" data-mp="2vzvyblk9zaac">
        {DEVICES.map(device => <button key={device.value} type="button" aria-pressed={list.includes(device.value)} onClick={() => apply(toggleDevice(current, device.value))} className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${list.includes(device.value) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-surface'}`} data-id="element-389" data-mp="3aq38le04pmzy">
            {device.label}
          </button>)}
      </div>;
  }
  return <div className="mt-5 space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-390" data-mp="77owdc9wspwy">
      <h3 className="text-sm font-semibold text-foreground" data-id="element-391" data-mp="75chx93d0xon">Elementos flotantes</h3>

      {/* WhatsApp */}
      <section className="rounded-xl border border-border p-4" data-id="element-392" data-mp="3tewx219vw8mn">
        <div className="flex items-center justify-between gap-3" data-id="element-393" data-mp="3tdc2wcrherpb">
          <span className="flex items-center gap-2 text-sm font-medium text-foreground" data-id="element-394" data-mp="3fn8kjwvq3i55">
            <MessageCircleIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-395" data-mp="3keqomtt1lyuc" />
            WhatsApp flotante
          </span>
          <Switch id="wa-enabled" label="Activar" checked={Boolean(whatsapp.enabled)} onChange={checked => setWhatsapp({
          enabled: checked
        })} data-id="element-396" data-mp="35qndl6057wyo" />
        </div>
        {whatsapp.enabled && <div className="mt-4 space-y-3" data-id="element-397" data-mp="2wpyzycenvymb">
            <Input id="wa-phone" label="Teléfono (con prefijo)" placeholder="34600123456" value={whatsapp.phone ?? ''} onChange={e => setWhatsapp({
          phone: e.target.value
        })} data-id="element-398" data-mp="2wr0g2vajbbaw" />
            {!(whatsapp.phone ?? '').replace(/\D/g, '') && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700" data-id="element-399" data-mp="2oozrqksuz9nd">
                Escribe el teléfono para que el botón aparezca en la landing.
              </p>}
            <Input id="wa-message" label="Mensaje inicial" placeholder="Hola, me interesa el Sistema AVA" value={whatsapp.message ?? ''} onChange={e => setWhatsapp({
          message: e.target.value
        })} data-id="element-400" data-mp="2fw68yde7ds4r" />
            <Input id="wa-label" label="Texto junto al icono (vacío = solo icono)" value={whatsapp.label ?? ''} onChange={e => setWhatsapp({
          label: e.target.value
        })} data-id="element-401" data-mp="25x77beruuwr9" />
            <Switch id="wa-card" label="Mostrar tarjeta al pulsar" checked={whatsapp.showCard !== false} onChange={checked => setWhatsapp({
          showCard: checked
        })} data-id="element-402" data-mp="1wxadkyqlz7pc" />
            {whatsapp.showCard !== false && <>
                <Input id="wa-card-title" label="Título de la tarjeta" placeholder="¿Tienes alguna duda?" value={whatsapp.cardTitle ?? ''} onChange={e => setWhatsapp({
            cardTitle: e.target.value
          })} data-id="element-403" data-mp="3rbbdlmxwa0r0" />
                <Input id="wa-card-text" label="Texto de la tarjeta" placeholder="Escríbeme por WhatsApp" value={whatsapp.cardText ?? ''} onChange={e => setWhatsapp({
            cardText: e.target.value
          })} data-id="element-404" data-mp="1i00wvsks72eq" />
              </>}
            <Select id="wa-position" label="Posición" options={POSITIONS} value={whatsapp.position ?? 'derecha'} onChange={e => setWhatsapp({
          position: e.target.value as 'derecha' | 'izquierda'
        })} data-id="element-405" data-mp="u4g4s40wdkgu" />
            <Input id="wa-size" type="number" min={40} max={72} label="Tamaño del botón (px)" value={String(whatsapp.size ?? 48)} onChange={e => setWhatsapp({
          size: Number(e.target.value)
        })} data-id="element-406" data-mp="134l73zr8hso5" />
            <div data-id="element-407" data-mp="2ej88vttrcoti">
              <p className="mb-2 text-sm font-medium text-foreground" data-id="element-408" data-mp="2ejrrzdv59j94">Mostrar en</p>
              {deviceRow(whatsapp.devices, next => setWhatsapp({
            devices: next
          }))}
            </div>
          </div>}
      </section>

      {/* Notificaciones de actividad */}
      <section className="rounded-xl border border-border p-4" data-id="element-409" data-mp="fehxxj8lkjue">
        <div className="flex items-center justify-between gap-3" data-id="element-410" data-mp="tespypln84h5">
          <span className="flex items-center gap-2 text-sm font-medium text-foreground" data-id="element-411" data-mp="tcyee1w9hopq">
            <BellIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-412" data-mp="ogr5q1chdz43" />
            Notificaciones de actividad
          </span>
          {/* Al activar se crean varios avisos neutros y editables: sin ningún
              mensaje el elemento no tendría nada que mostrar. */}
          <Switch id="act-enabled" label="Activar" checked={Boolean(activity.enabled)} onChange={checked => setActivity({
          enabled: checked,
          messages: checked && messages.length === 0 ? DEFAULT_MESSAGES.map(m => ({
            ...m
          })) : messages,
          max: checked ? activity.max ?? DEFAULT_MESSAGES.length : activity.max
        })} data-id="element-413" data-mp="agykgljq4b3i" />
        </div>
        {activity.enabled && <div className="mt-4 space-y-3" data-id="element-414" data-mp="1oml972suko0e">
            <Select id="act-mode" label="Origen de los avisos" options={[{
          value: 'manual',
          label: 'Mensajes que yo defina'
        }, {
          value: 'real',
          label: 'Actividad real del sistema'
        }]} value={activity.mode ?? 'manual'} onChange={e => setActivity({
          mode: e.target.value as 'manual' | 'real'
        })} data-id="element-415" data-mp="1teuug5j0ozwc" />
            <div className="space-y-2" data-id="element-416" data-mp="27ffawv1gmjmo">
              <p className="text-sm font-medium text-foreground" data-id="element-417" data-mp="2cdgzfvhnnb5j">Mensajes</p>
              {messages.map((message, index) => <div key={index} className="space-y-2 rounded-xl border border-border p-3" data-id="element-418" data-mp="1yeqyg4rpsrbw" data-mpi={index}>
                  <Input id={`act-title-${index}`} label="Título" placeholder="Nueva solicitud recibida" value={message.title} onChange={e => setActivity({
              messages: messages.map((m, i) => i === index ? {
                ...m,
                title: e.target.value
              } : m)
            })} data-id="element-419" data-mp="23cj03qi5vrta" />
                  <Input id={`act-text-${index}`} label="Texto" placeholder="Empresa de Madrid" value={message.text ?? ''} onChange={e => setActivity({
              messages: messages.map((m, i) => i === index ? {
                ...m,
                text: e.target.value
              } : m)
            })} data-id="element-420" data-mp="1ip0b5654u8vt" />
                  <Input id={`act-time-${index}`} label="Tiempo" placeholder="Hace unos minutos" value={message.time ?? ''} onChange={e => setActivity({
              messages: messages.map((m, i) => i === index ? {
                ...m,
                time: e.target.value
              } : m)
            })} data-id="element-421" data-mp="14pheb9hrujyt" />
                  <Button variant="ghost" size="sm" icon={Trash2Icon} onClick={() => setActivity({
              messages: messages.filter((_, i) => i !== index)
            })} data-id="element-422" data-mp="1skaga2qtuwdt">
                    Eliminar
                  </Button>
                </div>)}
              <Button variant="secondary" size="sm" icon={PlusIcon} onClick={() => setActivity({
            messages: [...messages, {
              title: 'Nueva solicitud recibida',
              text: 'Empresa de Madrid',
              time: 'Hace unos minutos'
            }]
          })} data-id="element-423" data-mp="2gbfu6cd7jelh">
                Añadir mensaje
              </Button>
            </div>
            <Select id="act-position" label="Posición" options={POSITIONS} value={activity.position ?? 'izquierda'} onChange={e => setActivity({
          position: e.target.value as 'derecha' | 'izquierda'
        })} data-id="element-424" data-mp="2xnecaspvqd26" />
            <div className="grid grid-cols-2 gap-3" data-id="element-425" data-mp="3bkjcwd6tqjq1">
              <Input id="act-delay" type="number" min={1} label="Primer aviso (s)" value={String(activity.delay ?? 6)} onChange={e => setActivity({
            delay: Number(e.target.value)
          })} data-id="element-426" data-mp="3bj004wkdmqt0" />
              <Input id="act-duration" type="number" min={2} label="Duración (s)" value={String(activity.duration ?? 6)} onChange={e => setActivity({
            duration: Number(e.target.value)
          })} data-id="element-427" data-mp="3lcp5np4wh56a" />
              <Input id="act-interval" type="number" min={4} label="Intervalo (s)" value={String(activity.interval ?? 18)} onChange={e => setActivity({
            interval: Number(e.target.value)
          })} data-id="element-428" data-mp="3u8y533svo3sf" />
              <Input id="act-max" type="number" min={1} label="Máximo por visita" value={String(activity.max ?? 3)} onChange={e => setActivity({
            max: Number(e.target.value)
          })} data-id="element-429" data-mp="1ixcc0y9i4e13" />
            </div>
            <Switch id="act-time" label="Mostrar el tiempo" checked={activity.showTime !== false} onChange={checked => setActivity({
          showTime: checked
        })} data-id="element-430" data-mp="1snotghm0rr30" />
            <div data-id="element-431" data-mp="21nu02pje2lg4">
              <p className="mb-2 text-sm font-medium text-foreground" data-id="element-432" data-mp="21nbwf7ued4y6">Mostrar en</p>
              {deviceRow(activity.devices, next => setActivity({
            devices: next
          }))}
            </div>
          </div>}
      </section>
    </div>;
}